import type { ThemeColorName } from '@examdojo/styles';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { StyleResolver } from './style-resolver.model';

/**
 * Resolver for curve/functiongraph styles.
 * Maps: { color, strokeWidth, dash, visible, opacity } → JSXGraph curve attributes
 */
export class CurveStyleResolver implements StyleResolver {
  readonly key = 'curve';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['color'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['strokeWidth'] !== undefined) {
      attrs['strokeWidth'] = style['strokeWidth'];
    }
    if (style['dash'] !== undefined) {
      attrs['dash'] = style['dash'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }
    if (style['opacity'] !== undefined) {
      attrs['strokeOpacity'] = style['opacity'];
    }

    return attrs;
  }
}

/**
 * Resolver for point styles.
 * Maps: { color, strokeColor, strokeWidth, size, visible, opacity } → JSXGraph point attributes
 */
export class PointStyleResolver implements StyleResolver {
  readonly key = 'point';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['color'] !== undefined) {
      attrs['fillColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['strokeColor'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['strokeColor'] as ThemeColorName);
      attrs['strokeWidth'] = style['strokeWidth'] ?? 1;
    }
    if (style['size'] !== undefined) {
      attrs['size'] = style['size'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }
    if (style['opacity'] !== undefined) {
      attrs['fillOpacity'] = style['opacity'];
    }

    return attrs;
  }
}

/**
 * Resolver for line styles.
 * Maps: { color, strokeWidth, dash, visible } → JSXGraph line attributes
 */
export class LineStyleResolver implements StyleResolver {
  readonly key = 'line';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['color'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['strokeWidth'] !== undefined) {
      attrs['strokeWidth'] = style['strokeWidth'];
    }
    if (style['dash'] !== undefined) {
      attrs['dash'] = style['dash'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }

    return attrs;
  }
}

/**
 * Resolver for polygon/area styles.
 * Maps: { color, fillColor, strokeColor, strokeWidth, opacity, visible } → JSXGraph polygon attributes
 */
export class PolygonStyleResolver implements StyleResolver {
  readonly key = 'polygon';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['fillColor'] !== undefined) {
      attrs['fillColor'] = themeService.getColor(style['fillColor'] as ThemeColorName);
    } else if (style['color'] !== undefined) {
      attrs['fillColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['strokeColor'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['strokeColor'] as ThemeColorName);
    }
    if (style['strokeWidth'] !== undefined) {
      attrs['strokeWidth'] = style['strokeWidth'];
    }
    if (style['opacity'] !== undefined) {
      attrs['fillOpacity'] = style['opacity'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }

    return attrs;
  }
}

/**
 * Resolver for label/text styles.
 * Maps: { color, fontSize, visible } → JSXGraph text attributes
 */
export class LabelStyleResolver implements StyleResolver {
  readonly key = 'label';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['color'] !== undefined) {
      const color = themeService.getColor(style['color'] as ThemeColorName);
      attrs['cssStyle'] = `color: ${color};`;
    }
    if (style['fontSize'] !== undefined) {
      attrs['fontSize'] = style['fontSize'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }

    return attrs;
  }
}

/**
 * Resolver for angle styles.
 * Maps: { color, strokeColor, radius, visible } → JSXGraph angle attributes
 */
export class AngleStyleResolver implements StyleResolver {
  readonly key = 'angle';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['color'] !== undefined) {
      attrs['fillColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['strokeColor'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['strokeColor'] as ThemeColorName);
    }
    if (style['radius'] !== undefined) {
      attrs['radius'] = style['radius'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }

    return attrs;
  }
}

/**
 * Resolver for circle styles.
 * Maps: { color, fillColor, strokeColor, strokeWidth, opacity, visible } → JSXGraph circle attributes
 */
export class CircleStyleResolver implements StyleResolver {
  readonly key = 'circle';

  resolve(style: Record<string, unknown>, themeService: JsxgraphThemeService): Record<string, unknown> {
    const attrs: Record<string, unknown> = {};

    if (style['strokeColor'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['strokeColor'] as ThemeColorName);
    } else if (style['color'] !== undefined) {
      attrs['strokeColor'] = themeService.getColor(style['color'] as ThemeColorName);
    }
    if (style['fillColor'] !== undefined) {
      attrs['fillColor'] = themeService.getColor(style['fillColor'] as ThemeColorName);
    }
    if (style['strokeWidth'] !== undefined) {
      attrs['strokeWidth'] = style['strokeWidth'];
    }
    if (style['opacity'] !== undefined) {
      attrs['fillOpacity'] = style['opacity'];
    }
    if (style['visible'] !== undefined) {
      attrs['visible'] = style['visible'];
    }

    return attrs;
  }
}
